'use client';

import { forwardRef } from 'react';
import type { ReceiptTemplate, ReceiptSection } from '@/lib/templates/schema';
import { ReceiptPaper } from './ReceiptPaper';
import {
  HeaderSection,
  TwoColumnSection,
  ItemsListSection,
  TotalsSection,
  PaymentSection,
  DateTimeSection,
  CustomMessageSection,
  BarcodeSection,
  DividerSection,
} from './sections';

interface ReceiptCanvasProps {
  template: ReceiptTemplate;
  className?: string;
}

function SectionRenderer({
  section,
  settings,
}: {
  section: ReceiptSection;
  settings: ReceiptTemplate['settings'];
}) {
  if (!section.enabled) return null;

  switch (section.type) {
    case 'header':
      return <HeaderSection config={section.config} settings={settings} />;
    case 'two-column':
      return <TwoColumnSection config={section.config} settings={settings} />;
    case 'items-list':
      return <ItemsListSection config={section.config} settings={settings} />;
    case 'totals':
      return <TotalsSection config={section.config} settings={settings} />;
    case 'payment':
      return <PaymentSection config={section.config} settings={settings} />;
    case 'datetime':
      return <DateTimeSection config={section.config} settings={settings} />;
    case 'custom-message':
      return <CustomMessageSection config={section.config} settings={settings} />;
    case 'barcode':
      return <BarcodeSection config={section.config} settings={settings} />;
    case 'divider':
      return <DividerSection config={section.config} settings={settings} />;
    default:
      return null;
  }
}

export const ReceiptCanvas = forwardRef<HTMLDivElement, ReceiptCanvasProps>(
  function ReceiptCanvas({ template, className = '' }, ref) {
    return (
      <ReceiptPaper ref={ref} settings={template.settings} className={className}>
        {template.sections
          .filter((section) => section.enabled)
          .map((section) => (
            <div key={section.id} data-section-id={section.id} className="cursor-pointer">
              <SectionRenderer
                section={section}
                settings={template.settings}
              />
            </div>
          ))}
      </ReceiptPaper>
    );
  }
);

